import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { debounceTime, filter, map, tap, withLatestFrom } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Area } from "src/model/class/general classes/area";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { TypeIntegration } from "src/model/enum/enumDigitalPlace";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { areaLoadInitStartAction } from "src/state/actions/area.actions";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { AlertService } from "./alert.service";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";

@Injectable({
  providedIn: "root",
})
export class AreaService implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "area.service";
  private _areaTreeStore: GenericApiData = null;
  private _areaTreeSubject: BehaviorSubject<Array<Area>> = new BehaviorSubject<
    Array<Area>
  >(null);

  private _isErrorNotSupported: boolean = false;
  private _isErrorInAreaRequest: boolean = false;

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;
  _eventSubscriptions: Array<Subscription> = null;

  public needToInitializa: boolean = true;
  public readonly areaTreeList = this._areaTreeSubject.asObservable();

  get IsErrorInGetAccessHistoryListAPIRequest(): boolean {
    return this._isErrorInAreaRequest;
  }

  get IsErrorNotSupported(): boolean {
    return this._isErrorNotSupported;
  }

  constructor(
    private _http: HttpClient,
    private _mqttService: MqttClientService,
    private _eventService: EventService,
    private _loggerService: LoggerService,
    private _store: Store,
    private _alertService: AlertService
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this.subscribeEvents();
    this.subscribeMQTTAddresses();
  }

  /** Unsubscribe from observables */
  ngOnDestroy(): void {
    this._areaTreeSubject.next(null);
    this._areaTreeSubject.complete();

    this.unsubscribeEvents();
    this.unsubscribeToMQTTAddresses();
  }

  /** NOT IMPLEMENTED YET */
  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      result = true;
    }
    return result;
  }

  /** Subscribe to EventService observables */
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combinedArray: Array<any>) => combinedArray[1]),
        map((combinedArray: Array<any>) => combinedArray[0])
      ).subscribe(() => {
        this.reloadServiceData();
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        await this.unsubscribeToMQTTAddresses();

        this._areaTreeStore = null;
        this._areaTreeSubject.next(null);
        this.needToInitializa = true;
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );
  }

  /** Unsubscribe from all EventService observables */
  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) sub.unsubscribe();
  }
  /**
   *  Initialize public observable list trasmission:
   *  it should be call in the costructor of all component/page that you need.
   */
  public Init(): void {
    this._store.dispatch(areaLoadInitStartAction());
    if (this.needToInitializa) {
      this.needToInitializa = false;
    }
  }

  //** NOT IMPLEMENTED YET */
  async subscribeMQTTAddresses() { }

  /** NOT IMPLEMENTED YET */
  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this.Init();
  }

  /**
   * Get full list of areas
   *
   * @returns
   */
  public getAreas(): Observable<GenericApiData> {
    return this._http.get<GenericApiData>(
      environment.httpServerRoot + AppSettings.API_URL_AREA
    );
  }

  /**
   * Get area by id
   * @param id
   * @returns
   */
  public getAreaById(id: number): Promise<Area> {
    return this._http
      .get<Area>(
        environment.httpServerRoot + AppSettings.API_URL_AREA + "?id=" + id
      )
      .toPromise();
  }

  /**
   * Update area changes on db via API
   * @param area Area to update
   * @returns
   */
  public updateArea(area: Area): Observable<Area> {
    return this._http
      .put<Area>(environment.httpServerRoot + AppSettings.API_URL_AREA, area)
      .pipe<Area>(
        tap(() => {
          this.reloadServiceData();
        })
      );
  }

  /**
   * Get last modifed area
   *
   * @param areaStore Area store
   * @returns
   */
  public getAreasByLastModified(
    areaStore: GenericApiData
  ): Observable<GenericApiData> {
    const lastAPIModifiedString: string = areaStore?.lastAPIModified || "";
    const queryString: string =
      "?lastModified=" + encodeURIComponent(lastAPIModifiedString);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_AREA + queryString
      )
      .pipe(
        map((returnAreaStore: GenericApiData) => {
          //if initial resource isn't changed it return null value (returnNotificationStore = null)
          if (returnAreaStore) {
            areaStore = returnAreaStore;
          }

          return areaStore;
        })
      );
  }

  /** Get area tree, using filter as queryString */
  public GetTreeAreaList(instanceguid: string): Subscription {
    const queryString: string = "?instanceguid=" + instanceguid;

    return this._http
      .get<Array<Area>>(
        environment.httpServerRoot + AppSettings.API_URL_AREA_TREE + queryString
      )
      .subscribe(
        (returnAreaTree: Array<Area>) => {
          this._isErrorInAreaRequest = false;
          this._areaTreeSubject.next(returnAreaTree);
        },
        (error) => {
          this.handlePeculiarAPIResponseError(error);
          this._isErrorInAreaRequest = true;
          this._areaTreeStore = null;
          this._areaTreeSubject.next([]);
        }
      );
  }

  /** Handle peculiar API Response Error */
  private handlePeculiarAPIResponseError(error: any) {
    switch (error.status) {
      case ApiErrorType.NOT_FOUND:
        this._isErrorNotSupported = true;
        break;
      default:
        break;
    }
  }

  /**
   * Delete area
   * @param idArea
   * @returns
   */
  public deleteArea(idArea: number) {
    const queryString: string = "?id=" + idArea;

    return this._http
      .delete<Area>(
        environment.httpServerRoot + AppSettings.API_URL_AREA + queryString
      )
      .subscribe(
        () => {
          //nothing to do if it's ok
        },
        (error) => {
          this.handleApiResponseError();
        }
      );
  }

  /**
   * Insert area
   * @param idArea
   * @returns
   */
  public insertArea(insertArea: Area, idparent: number): Observable<Area> {
    return this._http.post<Area>(
      environment.httpServerRoot + AppSettings.API_URL_AREA + "/" + idparent,
      insertArea
    );
  }

  /**
   * Handle saving data for an external integration (CED House, iCond, ...) on a set of areas
   * @param typeIntegration integration to enable/disable (1 = CED House, 2 = iCond), sent in the body
   * @param body data to save
   * @returns promise
   */
  public saveIntegration(typeIntegration: TypeIntegration, body: { enabled: boolean, idAreas: number[] }): Promise<{ enabled: boolean, idAreas: number[], typeIntegration: TypeIntegration }> {
    return this._http.patch<{ enabled: boolean, idAreas: number[], typeIntegration: TypeIntegration }>(
      environment.httpServerRoot + AppSettings.API_URL_AREA + "/integration",
      { ...body, typeIntegration }
    ).toPromise();
  }

  /**
   * Show error message if something went wrong saving gateway settings or taking them
   */
  private async handleApiResponseError(): Promise<void> {
    this._alertService.presentAlertSomethingWrong();
  }
}
